import { useState, useEffect } from "react";

/**
 * useLastVisitedRow
 *
 * 用於「列表 → 明細 → 返回列表」情境。
 * 離開列表時呼叫 saveLastVisited 記錄 ID；
 * 返回列表時自動 highlight 對應列，並捲動到畫面中央。
 *
 * @param {object} options
 * @param {Array}  options.list             - 總覽資料列（用於判斷資料是否已載入）
 * @param {string} options.rowKey           - 資料列中比對用的欄位名稱（e.g. "cycleOrderID"）
 * @param {string} options.storageKey       - sessionStorage 使用的 key
 * @param {string} [options.highlightClass] - highlight 樣式的 CSS class，預設 "table-row-selected"
 *
 * @returns {{ saveLastVisited: (id: number|string) => void, bodyRowClass: object }}
 */
const useLastVisitedRow = ({ list, rowKey, storageKey, highlightClass = "table-row-selected" }) => {
	// 元件掛載時，從 sessionStorage 讀取上次進入的列 ID
	// lazy initializer 只執行一次，讀完立刻清除，避免頁面重整後仍殘留 highlight
	const [lastVisitedId] = useState(() => {
		const stored = sessionStorage.getItem(storageKey);
		sessionStorage.removeItem(storageKey);
		return stored ? Number(stored) : null; 	// sessionStorage 只存字串，轉回數字以利後續嚴格比對（===）
	});

	// 導向明細前呼叫此函式，將目標列 ID 暫存至 sessionStorage
	// 元件 unmount 後資料仍保留，返回時可正確取得
	const saveLastVisited = id => {
		sessionStorage.setItem(storageKey, id);
	};

	// 當列表資料載入完成後，將 highlight 列捲動到畫面中央
	useEffect(() => {
		if (!lastVisitedId || !list?.length) return;

		// setTimeout 讓 React 完成 DOM 渲染後再查詢目標元素
		const timer = setTimeout(() => {
			const el = document.querySelector(`.${highlightClass}`);
			if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
		}, 100);

		// cleanup：若 list 在 100ms 內再次更新，取消尚未執行的 timer
		return () => clearTimeout(timer);
	}, [list, lastVisitedId, highlightClass]);

	return {
		saveLastVisited, // 記錄當前列的 ID
		bodyRowClass: lastVisitedId
			? {
				key: rowKey, // 比對欄位名稱
				value: false, // 滿足 PropTypes（bool），此值永遠不會與 row ID 相等
				className: highlightClass, // highlight 樣式的 CSS class
				fn: resource => (resource[rowKey] === lastVisitedId ? highlightClass : ""), // 實際比對函式
			  }
			: undefined, // 沒有 ID 時回傳 undefined
	};
};

export default useLastVisitedRow;